import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const CATS = [
  { key: '', label: 'Alle', icon: '\uD83D\uDCC2' },
  { key: 'Vertrag', label: 'Vertraege', icon: '\uD83D\uDCDC' },
  { key: 'Bericht', label: 'Berichte', icon: '\uD83D\uDCCA' },
  { key: 'Strategie', label: 'Strategien', icon: '\uD83C\uDFAF' },
  { key: 'Sonstiges', label: 'Sonstiges', icon: '\uD83D\uDCC1' },
];

const TYPE_ICON = { pdf: '\uD83D\uDCD5', docx: '\uD83D\uDCD8', doc: '\uD83D\uDCD8', xlsx: '\uD83D\uDCD7', xls: '\uD83D\uDCD7', pptx: '\uD83D\uDCD9', txt: '\uD83D\uDCC4', md: '\uD83D\uDCDD', csv: '\uD83D\uDCCA' };
const STATUS_COLOR = { uploaded: '#e0af68', processing: '#7aa2f7', ready: '#9ece6a', error: '#f7768e' };

function formatSize(b) { if (!b) return '-'; const k = 1024; const s = ['B','KB','MB','GB']; const i = Math.floor(Math.log(b)/Math.log(k)); return (b/Math.pow(k,i)).toFixed(1)+' '+s[i]; }
function formatDate(d) { if (!d) return '-'; return new Date(d).toLocaleDateString('de-DE'); }

export default function Library() {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const nav = useNavigate();
  const inputRef = useRef(null);

  const fetchDocs = useCallback(async () => {
    setLoading(true);
    let url = `/api/documents?page=${page}&page_size=50`;
    if (catFilter) url += `&category=${catFilter}`;
    if (typeFilter) url += `&file_type=${typeFilter}`;
    try {
      const r = await fetch(url);
      if (r.ok) { const d = await r.json(); setData(d?.documents ? d : null); } else { setData(null); }
    } catch {}
    setLoading(false);
  }, [page, catFilter, typeFilter]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const handleUpload = useCallback(async (files) => {
    if (!files?.length) return;
    setUploading(true);
    const fd = new FormData();
    if (files.length === 1) {
      fd.append('file', files[0]);
      await fetch('/api/documents/upload', { method: 'POST', body: fd }).catch(() => {});
    } else {
      Array.from(files).forEach(f => fd.append('files', f));
      await fetch('/api/documents/bulk-upload', { method: 'POST', body: fd }).catch(() => {});
    }
    setUploading(false);
    fetchDocs();
  }, [fetchDocs]);

  const handleDelete = useCallback(async (id) => {
    await fetch(`/api/documents/${id}`, { method: 'DELETE' });
    fetchDocs();
  }, [fetchDocs]);

  const handleBulk = useCallback(async (action) => {
    if (!selected.size) return;
    await fetch('/api/documents/bulk', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ document_ids: [...selected], action }),
    });
    setSelected(new Set());
    fetchDocs();
  }, [selected, fetchDocs]);

  const docs = (data?.documents || []).filter(d => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (d.filename || '').toLowerCase().includes(q) || (d.title || '').toLowerCase().includes(q);
  });

  const totalPages = data ? Math.ceil((data.total || 0) / 50) : 1;

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}
      onDragOver={e => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={e => { e.preventDefault(); setDragOver(false); handleUpload(e.dataTransfer.files); }}
    >
      {/* Sidebar */}
      <div style={{ width: 180, minWidth: 180, borderRight: '1px solid var(--border)', padding: 'var(--gap-md)', display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-xs)', fontWeight: 600, textTransform: 'uppercase', marginBottom: 8 }}>Kategorien</div>
        {CATS.map(c => (
          <button key={c.key} onClick={() => { setCatFilter(c.key); setPage(1); }} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', background: catFilter === c.key ? 'rgba(187,154,247,0.15)' : 'transparent', border: 'none', borderRadius: 'var(--border-radius)', padding: '8px 10px', color: catFilter === c.key ? '#bb9af7' : 'var(--text-secondary)', cursor: 'pointer', fontSize: 'var(--font-size-sm)' }}>
            <span>{c.icon}</span> {c.label}
          </button>
        ))}
        <div style={{ marginTop: 16 }}>
          <select value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPage(1); }} style={{ width: '100%', background: 'var(--bg-tertiary)', border: '1px solid var(--border)', borderRadius: 'var(--border-radius)', padding: '6px 8px', color: 'var(--text-primary)', fontSize: 'var(--font-size-sm)' }}>
            <option value="">Alle Typen</option>
            {['pdf','docx','xlsx','pptx','csv','md','txt'].map(t => <option key={t} value={t}>{t.toUpperCase()}</option>)}
          </select>
        </div>
        <button onClick={() => inputRef.current?.click()} style={{ marginTop: 16, background: '#bb9af7', border: 'none', borderRadius: 'var(--border-radius)', padding: '8px 12px', color: '#1a1b26', fontWeight: 600, cursor: 'pointer', fontSize: 'var(--font-size-sm)' }}>
          Upload
        </button>
        <input ref={inputRef} type="file" multiple hidden onChange={e => handleUpload(e.target.files)} />
      </div>

      {/* Main */}
      <div style={{ flex: 1, overflow: 'auto', padding: 'var(--gap-md)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 'var(--gap-md)' }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Suchen..." style={{ flex: 1, maxWidth: 300, background: 'var(--bg-tertiary)', border: '1px solid var(--border)', borderRadius: 'var(--border-radius)', padding: '8px 12px', color: 'var(--text-primary)', fontSize: 'var(--font-size-sm)', outline: 'none' }} />
          <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-sm)' }}>{data?.total || 0} Dokumente</span>
          {selected.size > 0 && (
            <button onClick={() => handleBulk('delete')} style={{ background: 'rgba(247,118,142,0.15)', border: '1px solid rgba(247,118,142,0.3)', borderRadius: 'var(--border-radius)', padding: '6px 12px', color: '#f7768e', cursor: 'pointer', fontSize: 'var(--font-size-sm)' }}>
              {selected.size} loeschen
            </button>
          )}
        </div>

        {dragOver && <div style={{ border: '2px dashed #bb9af7', borderRadius: 'var(--border-radius-lg)', padding: 24, textAlign: 'center', color: '#bb9af7', marginBottom: 12 }}>Dateien hier ablegen</div>}
        {uploading && <div style={{ color: '#bb9af7', marginBottom: 12, fontSize: 'var(--font-size-sm)' }}>Uploading...</div>}

        <div style={{ display: 'grid', gap: 0 }}>
          {/* Header */}
          <div style={{ display: 'grid', gridTemplateColumns: '32px 1fr 60px 70px 70px 90px 90px 100px', gap: 8, padding: '8px 12px', borderBottom: '1px solid var(--border)', color: 'var(--text-muted)', fontSize: 'var(--font-size-xs)', fontWeight: 600, textTransform: 'uppercase' }}>
            <div><input type="checkbox" onChange={e => { if (e.target.checked) setSelected(new Set(docs.map(d => d.id))); else setSelected(new Set()); }} /></div>
            <div>Datei</div><div>Typ</div><div>Groesse</div><div>Status</div><div>Kategorie</div><div>Datum</div><div>Aktionen</div>
          </div>
          {loading && <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>Laden...</div>}
          {!loading && docs.length === 0 && <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>Keine Dokumente</div>}
          {docs.map(d => (
            <div key={d.id} onClick={() => nav(`/library/${d.id}`)} style={{ display: 'grid', gridTemplateColumns: '32px 1fr 60px 70px 70px 90px 90px 100px', gap: 8, padding: '10px 12px', borderBottom: '1px solid var(--border)', cursor: 'pointer', alignItems: 'center', transition: 'var(--transition-fast)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-tertiary)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <div onClick={e => e.stopPropagation()}><input type="checkbox" checked={selected.has(d.id)} onChange={e => { const s = new Set(selected); if (e.target.checked) s.add(d.id); else s.delete(d.id); setSelected(s); }} /></div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, overflow: 'hidden' }}>
                <span>{TYPE_ICON[d.file_type] || '\uD83D\uDCC4'}</span>
                <span style={{ color: 'var(--text-primary)', fontSize: 'var(--font-size-sm)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.title || d.filename}</span>
              </div>
              <div style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-xs)', textTransform: 'uppercase' }}>{d.file_type}</div>
              <div style={{ color: 'var(--text-secondary)', fontSize: 'var(--font-size-xs)' }}>{formatSize(d.file_size)}</div>
              <div><span style={{ background: `${STATUS_COLOR[d.status] || 'var(--text-muted)'}22`, color: STATUS_COLOR[d.status], borderRadius: 4, padding: '2px 6px', fontSize: 11 }}>{d.status}</span></div>
              <div style={{ color: 'var(--text-secondary)', fontSize: 'var(--font-size-xs)' }}>{d.category || '-'}</div>
              <div style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-xs)' }}>{formatDate(d.uploaded_at)}</div>
              <div onClick={e => e.stopPropagation()} style={{ display: 'flex', gap: 4 }}>
                <button onClick={() => nav(`/analyze?doc=${d.id}`)} title="Analyse" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14 }}>{'\uD83E\uDDE0'}</button>
                <button onClick={() => nav(`/chat?doc=${d.id}`)} title="Chat" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14 }}>{'\uD83D\uDCAC'}</button>
                <button onClick={() => window.open(`/api/documents/${d.id}/download`)} title="Download" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14 }}>{'\u2B07'}</button>
                <button onClick={() => handleDelete(d.id)} title="Loeschen" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14 }}>{'\uD83D\uDDD1'}</button>
              </div>
            </div>
          ))}
        </div>

        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 'var(--gap-md)' }}>
            <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', borderRadius: 'var(--border-radius)', padding: '6px 12px', color: 'var(--text-primary)', cursor: 'pointer', opacity: page <= 1 ? 0.4 : 1 }}>{'\u2190'}</button>
            <span style={{ color: 'var(--text-secondary)', padding: '6px 12px', fontSize: 'var(--font-size-sm)' }}>{page} / {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', borderRadius: 'var(--border-radius)', padding: '6px 12px', color: 'var(--text-primary)', cursor: 'pointer', opacity: page >= totalPages ? 0.4 : 1 }}>{'\u2192'}</button>
          </div>
        )}
      </div>
    </div>
  );
}
